/**
 * Utility functions for calculating reminder times
 */

/**
 * Calculate the next scheduled time for a reminder
 * @param reminderType - 0=Absolute, 1=Relative to due_date, 2=Relative to start_date, 3=Relative to created_on
 * @param absoluteDate - Absolute date/time (for reminderType = 0)
 * @param relativeOffsetMinutes - Offset in minutes (negative = before, positive = after)
 * @param taskDueDate - Due date of the task
 * @param taskStartDate - Start date of the task
 * @param taskCreatedOn - Created timestamp of the task
 * @returns Next scheduled time in milliseconds, or null if cannot be calculated
 */
export function calculateNextScheduledAt(
  reminderType: number,
  absoluteDate: number | null,
  relativeOffsetMinutes: number | null,
  taskDueDate?: number | null,
  taskStartDate?: number | null,
  taskCreatedOn?: number | null,
): number | null {
  // Absolute reminder
  if (reminderType === 0) {
    return absoluteDate
  }

  // Relative reminders require an offset
  if (relativeOffsetMinutes === null) {
    return null
  }

  let referenceDate: number | null = null

  // Determine reference date based on reminder type
  switch (reminderType) {
    case 1: // Relative to due_date
      referenceDate = taskDueDate ?? null
      break
    case 2: // Relative to start_date
      referenceDate = taskStartDate ?? null
      break
    case 3: // Relative to created_on
      referenceDate = taskCreatedOn ?? null
      break
    default:
      return null
  }

  if (referenceDate === null) {
    return null
  }

  // Calculate: referenceDate + offset (in milliseconds)
  const offsetMs = relativeOffsetMinutes * 60 * 1000
  return referenceDate + offsetMs
}

/**
 * Calculate the next scheduled time for a recurring reminder after it has been sent
 * @param lastSentAt - When the reminder was last sent
 * @param recurrenceType - 0=daily, 1=weekly, 2=monthly, 3=yearly
 * @param recurrenceInterval - How often to recur (e.g., every 2 days)
 * @param recurrenceEndDate - Optional end date for recurrence
 * @param recurrenceMaxOccurrences - Optional max number of occurrences
 * @param occurrenceCount - Current occurrence count
 * @returns Next scheduled time for recurring reminder, or null if recurrence has ended
 */
export function calculateNextRecurringSchedule(
  lastSentAt: number,
  recurrenceType: number | null,
  recurrenceInterval: number | null,
  recurrenceEndDate: number | null,
  recurrenceMaxOccurrences: number | null,
  occurrenceCount: number,
): number | null {
  if (recurrenceType === null || recurrenceInterval === null) {
    return null
  }

  // Check if max occurrences reached
  if (
    recurrenceMaxOccurrences !== null &&
    occurrenceCount >= recurrenceMaxOccurrences
  ) {
    return null
  }

  const lastSentDate = new Date(lastSentAt)
  let nextDate: Date

  switch (recurrenceType) {
    case 0: // Daily
      nextDate = new Date(lastSentDate)
      nextDate.setDate(nextDate.getDate() + recurrenceInterval)
      break
    case 1: // Weekly
      nextDate = new Date(lastSentDate)
      nextDate.setDate(nextDate.getDate() + recurrenceInterval * 7)
      break
    case 2: // Monthly
      nextDate = new Date(lastSentDate)
      nextDate.setMonth(nextDate.getMonth() + recurrenceInterval)
      break
    case 3: // Yearly
      nextDate = new Date(lastSentDate)
      nextDate.setFullYear(nextDate.getFullYear() + recurrenceInterval)
      break
    default:
      return null
  }

  const nextScheduled = nextDate.getTime()

  // Check if past end date
  if (recurrenceEndDate !== null && nextScheduled > recurrenceEndDate) {
    return null
  }

  return nextScheduled
}
